import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../aws-config';

/**
 * Service for interacting with the admin API
 */
export const adminService = {
  /**
   * Get the list of available tables
   * @returns Promise with the list of tables
   */
  async getTables(): Promise<string[]> {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(`${API_URL}/admin/tables`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken.toString()}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch tables: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.tables;
  },
  
  /**
   * Get all items from a table
   * @param tableName The name of the table
   * @returns Promise with the items
   */
  async getItems(tableName: string): Promise<any[]> {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(`${API_URL}/admin/tables/${tableName}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken.toString()}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch items: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.items;
  },
  
  /**
   * Get a specific item from a table
   * @param tableName The name of the table
   * @param id The ID of the item
   * @returns Promise with the item
   */
  async getItem(tableName: string, id: string): Promise<any> {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(`${API_URL}/admin/tables/${tableName}/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken.toString()}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch item: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.item;
  },
  
  /**
   * Create a new item in a table
   * @param tableName The name of the table
   * @param item The item to create
   * @returns Promise with the created item
   */
  async createItem(tableName: string, item: any): Promise<any> {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(`${API_URL}/admin/tables/${tableName}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken.toString()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ item }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create item: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.item;
  },
  
  /**
   * Update an item in a table
   * @param tableName The name of the table
   * @param id The ID of the item
   * @param item The updated item
   * @returns Promise with the updated item
   */
  async updateItem(tableName: string, id: string, item: any): Promise<any> {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(`${API_URL}/admin/tables/${tableName}/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken.toString()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ item }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update item: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.item;
  },
  
  /**
   * Delete an item from a table
   * @param tableName The name of the table
   * @param id The ID of the item
   * @returns Promise with the success message
   */
  async deleteItem(tableName: string, id: string): Promise<any> {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(`${API_URL}/admin/tables/${tableName}/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken.toString()}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete item: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  },
};
